import { isDevMode } from '@angular/core';
import { SseMessage } from '../sse/sse.models';
import {
  IAiInvestmentAnalysisEvent,
  IChartPayload,
  IGroupedBarSeries,
  INamedValue,
  RESPONSE_ERROR_CODES,
  RESPONSE_STAGES,
} from './ai-investment-analysis.models';

/*
 * Maps raw SSE messages to typed events and validates everything the UI renders or sends back.
 * Anything unusable is skipped with a dev-mode warning instead of breaking the stream,
 * so backend changes show up in the console rather than as broken UI.
 */

const warnedKeys = new Set<string>();

/** Dev-only diagnostics: logs each distinct problem once, with the raw event data. */
function warnOnce(reason: string, msg: SseMessage): void {
  if (!isDevMode() || warnedKeys.has(reason)) {
    return;
  }
  warnedKeys.add(reason);
  console.warn(`[ai-analysis] ${reason}`, msg.data);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isArrayOf<T>(value: unknown, isItem: (item: unknown) => item is T): value is readonly T[] {
  return Array.isArray(value) && value.every(isItem);
}

function isNamedValue(value: unknown): value is INamedValue {
  return isRecord(value) && isString(value['name']) && isFiniteNumber(value['value']);
}

function isSeries(value: unknown): value is IGroupedBarSeries {
  return isRecord(value) && isString(value['name']) && isArrayOf(value['values'], isFiniteNumber);
}

function stringOrNull(value: unknown): string | null {
  return isString(value) ? value : null;
}

/** Returns a known enum value, or null (with a dev warning) for a value this client version doesn't know yet. */
function knownOrNull<T extends string>(value: unknown, known: readonly T[], field: string, msg: SseMessage): T | null {
  const match = known.find((item) => item === value);
  if (match === undefined) {
    warnOnce(`Unknown ${field} ${JSON.stringify(value)}, shown generically`, msg);
    return null;
  }
  return match;
}

function parseChartPayload(suggestedType: unknown, data: unknown): IChartPayload | null {
  if (!isRecord(data)) {
    return null;
  }
  switch (suggestedType) {
    case 'TREEMAP': {
      const nodes = data['nodes'];
      return isArrayOf(nodes, isNamedValue) ? { suggestedType: 'TREEMAP', data: { nodes } } : null;
    }
    case 'PIE': {
      const slices = data['slices'];
      return isArrayOf(slices, isNamedValue) ? { suggestedType: 'PIE', data: { slices } } : null;
    }
    case 'GROUPED_BAR': {
      const categories = data['categories'];
      const series = data['series'];
      if (!isArrayOf(categories, isString) || !isArrayOf(series, isSeries)) {
        return null;
      }
      // Values are aligned by index with categories; a length mismatch would draw misaligned bars
      return series.every((s) => s.values.length === categories.length)
        ? { suggestedType: 'GROUPED_BAR', data: { categories, series } }
        : null;
    }
    default:
      return null;
  }
}

/** Maps one raw SSE message to a typed event. Returns null (with a dev-mode warning) for anything unusable. */
export function parseAnalysisEvent(msg: SseMessage): IAiInvestmentAnalysisEvent | null {
  let raw: unknown;
  try {
    raw = JSON.parse(msg.data);
  } catch {
    warnOnce(`Event "${msg.event}": data is not valid JSON, skipped`, msg);
    return null;
  }
  if (!isRecord(raw)) {
    warnOnce(`Event "${msg.event}": data is not a JSON object, skipped`, msg);
    return null;
  }

  // The contract's discriminator is the payload's `type` field (it matches the SSE `event:` line).
  const type = raw['type'];
  switch (type) {
    case 'response.started': {
      const sessionId = stringOrNull(raw['sessionId']);
      if (sessionId === null) {
        warnOnce('response.started without "sessionId", skipped', msg);
        return null;
      }
      return { type: 'response.started', responseId: stringOrNull(raw['responseId']), sessionId };
    }

    case 'response.update':
      return { type: 'response.update', stage: knownOrNull(raw['stage'], RESPONSE_STAGES, 'stage', msg) };

    case 'response.text': {
      const text = stringOrNull(raw['text']);
      if (text === null) {
        warnOnce('response.text without "text", skipped', msg);
        return null;
      }
      return { type: 'response.text', text };
    }

    case 'response.chart': {
      const chartId = stringOrNull(raw['chartId']);
      const title = stringOrNull(raw['title']);
      const payload = parseChartPayload(raw['suggestedType'], raw['data']);
      if (chartId === null || title === null || payload === null) {
        warnOnce(`response.chart ${JSON.stringify(raw['chartId'])} with missing or unsupported fields, skipped`, msg);
        return null;
      }
      return { type: 'response.chart', chart: { chartId, meaning: stringOrNull(raw['meaning']), title, ...payload } };
    }

    case 'response.completed': {
      const disclaimer = stringOrNull(raw['disclaimer']);
      if (disclaimer === null) {
        // The disclaimer is mandatory: an analysis without it must not be shown as completed
        warnOnce('response.completed without the mandatory "disclaimer", skipped', msg);
        return null;
      }
      return { type: 'response.completed', disclaimer };
    }

    case 'response.failed':
      return {
        type: 'response.failed',
        errorCode: knownOrNull(raw['errorCode'], RESPONSE_ERROR_CODES, 'errorCode', msg),
        retryable: raw['retryable'] === true,
      };

    default:
      warnOnce(`Unknown event type ${JSON.stringify(type)}, skipped`, msg);
      return null;
  }
}
